import { useState, useCallback } from 'react';
import api from '@/services/api';
import { toast } from 'sonner';
import { manageErrors } from '@/lib/manageErrors';
import {
  Permission,
  Role,
  AccessGroup,
  User,
  GroupMember,
  CreatePermissionData,
  UpdatePermissionData,
  CreateRoleData,
  UpdateRoleData,
  CreateAccessGroupData,
  UpdateAccessGroupData,
  AddMembersData,
  RemoveMembersData,
} from '@/types/iam';

// Permissions Hooks
export const useListPermissions = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/security/permissions');
      setPermissions(response.data);
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error fetching permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { permissions, fetchPermissions, loading };
};

export const useCreatePermission = () => {
  const [loading, setLoading] = useState(false);

  const createPermission = useCallback(async (data: CreatePermissionData) => {
    setLoading(true);
    try {
      const response = await api.post('/api/v1/security/permissions', data);
      toast.success('Permission created successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error creating permission');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createPermission, loading };
};

export const useUpdatePermission = () => {
  const [loading, setLoading] = useState(false);

  const updatePermission = useCallback(async (data: UpdatePermissionData) => {
    setLoading(true);
    try {
      const response = await api.put(`/api/v1/security/permissions/${data.id}`, data);
      toast.success('Permission updated successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error updating permission');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updatePermission, loading };
};

export const useDeletePermission = () => {
  const [loading, setLoading] = useState(false);

  const deletePermission = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/v1/security/permissions/${id}`);
      toast.success('Permission deleted successfully');
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error deleting permission');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deletePermission, loading };
};

// Roles Hooks
export const useListRoles = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/security/roles');
      setRoles(response.data);
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error fetching roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { roles, fetchRoles, loading };
};

export const useCreateRole = () => {
  const [loading, setLoading] = useState(false);

  const createRole = useCallback(async (data: CreateRoleData) => {
    setLoading(true);
    try {
      const response = await api.post('/api/v1/security/roles', data);
      toast.success('Role created successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error creating role');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createRole, loading };
};

export const useUpdateRole = () => {
  const [loading, setLoading] = useState(false);

  const updateRole = useCallback(async (data: UpdateRoleData) => {
    setLoading(true);
    try {
      const response = await api.put(`/api/v1/security/roles/${data.id}`, data);
      toast.success('Role updated successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error updating role');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateRole, loading };
};

export const useDeleteRole = () => {
  const [loading, setLoading] = useState(false);

  const deleteRole = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/v1/security/roles/${id}`);
      toast.success('Role deleted successfully');
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error deleting role');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteRole, loading };
};

// Access Groups Hooks
export const useListAccessGroups = () => {
  const [accessGroups, setAccessGroups] = useState<AccessGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAccessGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/security/groups');
      setAccessGroups(response.data);
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error fetching access groups');
      setAccessGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { accessGroups, fetchAccessGroups, loading };
};

export const useCreateAccessGroup = () => {
  const [loading, setLoading] = useState(false);

  const createAccessGroup = useCallback(async (data: CreateAccessGroupData) => {
    setLoading(true);
    try {
      const response = await api.post('/api/v1/security/groups', data);
      toast.success('Access group created successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error creating access group');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createAccessGroup, loading };
};

export const useUpdateAccessGroup = () => {
  const [loading, setLoading] = useState(false);

  const updateAccessGroup = useCallback(async (data: UpdateAccessGroupData) => {
    setLoading(true);
    try {
      const response = await api.put(`/api/v1/security/groups/${data.id}`, data);
      toast.success('Access group updated successfully');
      return response.data;
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error updating access group');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateAccessGroup, loading };
};

export const useDeleteAccessGroup = () => {
  const [loading, setLoading] = useState(false);

  const deleteAccessGroup = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/v1/security/groups/${id}`);
      toast.success('Access group deleted successfully');
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error deleting access group');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteAccessGroup, loading };
};

// Users Hooks
export const useListUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/security/users');
      setUsers(response.data);
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error fetching users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { users, fetchUsers, loading };
};

// Group Members Hooks
export const useListGroupMembers = () => {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGroupMembers = useCallback(async (groupId: string) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/security/groups/${groupId}/members`);
      setMembers(response.data);
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error fetching group members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { members, fetchGroupMembers, loading };
};

export const useAddGroupMembers = () => {
  const [loading, setLoading] = useState(false);

  const addGroupMembers = useCallback(async (groupId: string, data: AddMembersData) => {
    setLoading(true);
    try {
      await api.post(`/api/v1/security/groups/${groupId}/members/add`, data);
      toast.success('Members added successfully');
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error adding members');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { addGroupMembers, loading };
};

export const useRemoveGroupMembers = () => {
  const [loading, setLoading] = useState(false);

  const removeGroupMembers = useCallback(async (groupId: string, data: RemoveMembersData) => {
    setLoading(true);
    try {
      await api.post(`/api/v1/security/groups/${groupId}/members/remove`, data);
      toast.success('Members removed successfully');
    } catch (error: any) {
      toast.error(manageErrors(error?.status) ?? 'Error removing members');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { removeGroupMembers, loading };
};
